import { Person as GraphQlPerson, GRAPHQL_TYPE_PERSON } from "@radivision/graphql/lib/ts/graphql/person";
import React from "react";
import { connect } from "react-redux";
import { ProfileHeader } from "../profile-header/profile-header";
import { PersonDetails } from "./person-details";
import { SideBar } from "./sidebar";
import { fetchPerson, cacheData } from "../../../redux/actions";
import { AppState } from "../../../redux";
import { FetchPersonAction, CacheDataAction, FetchPersonPayload, CacheDataPayload } from "../../../redux/cache/types";
import { ImageHelper } from "../../../utilities/image-helper";
import { PreviewKind, TopLevelMediaKind } from "@radivision/graphql";
import { getProfileInfo } from "../../../utilities/get-profile-info";
import { ProfileMetaTags } from "../../seo/profile-meta";
import { buildPersonMeta, selectCurrentRole } from "../../../utilities/profile-meta";
import { PAGE_TITLE_CONTEXT } from "../../../contexts/page-title-context";
interface ComponentProps {
  list?: GraphQlPerson;
}

interface MapDispatchProps {
  fetchPerson: (payload: FetchPersonPayload) => FetchPersonAction;
  cacheData: (payload: CacheDataPayload) => CacheDataAction;
}

interface MapStateProps {
  user: GraphQlPerson | null;
  person: GraphQlPerson | null;
  inProgress: boolean;
}

type Props = ComponentProps & MapDispatchProps & MapStateProps;

const PersonController = (props: Props) => {
  const { person, list, inProgress, fetchPerson, cacheData, user } = props;
  const id = list?.id;
  const isOwner = user && person && user?.id === person?.id;

  React.useEffect(() => {
    if (!person && !inProgress) {
      fetchPerson({ id });
    }
  }, [id, person, fetchPerson, inProgress]);

  const updateContext = React.useCallback((person: GraphQlPerson) => cacheData({ key: person?.id, data: person }), [
    cacheData,
  ]);
  const previewUrl = person?.previews
    ? ImageHelper.fetchOptimizedImageUrl({
        imageType: "PREVIEW",
        preview: {
          content: person.previews,
          previewKind: PreviewKind.HEADSHOT,
          topLevelMedia: TopLevelMediaKind.IMAGE,
        },
        desiredDimensions: { containerWidthRatio: 3 / 4, numberOfItems: 1 },
        isSquareImage: false,
        revision: person.revision,
      })
    : null;
  const { profileUrl } = getProfileInfo(person);
  const { setPageTitle } = React.useContext(PAGE_TITLE_CONTEXT);

  // person is undefined until its fetch resolves; rendering the tags before then put the
  // literal string "undefined" in the title, so hold off until there is a profile.
  const meta = person?.fullName
    ? buildPersonMeta(person.fullName, person.responsibility, selectCurrentRole(person))
    : null;
  const metaTitle = meta?.title;

  React.useEffect(() => {
    if (metaTitle) {
      setPageTitle(metaTitle);
    }
  }, [metaTitle, setPageTitle]);

  return (
    <div>
      {meta ? (
        <ProfileMetaTags meta={meta} canonicalPath={profileUrl} imageUrl={previewUrl?.requestedResolutionUrl} />
      ) : null}
      <ProfileHeader type={GRAPHQL_TYPE_PERSON} value={person} owner={isOwner} updateContext={updateContext} />

      <div className="profile-body">
        <SideBar {...props} updateContext={updateContext} />
        <PersonDetails {...props} person={person} user={user} updateContext={updateContext} />
      </div>
    </div>
  );
};

const mapDispath = {
  fetchPerson: fetchPerson,
  cacheData: cacheData,
};

const mapState = (state: AppState, props: ComponentProps) => ({
  user: state.account.user,
  person: props?.list?.id ? state.cache.store[props.list.id] : null,
});

export const PersonControllerContainer = connect(mapState, mapDispath)(PersonController);
